export const SWIPE_THRESHOLD_PX = 56;
export const SWIPE_VELOCITY_THRESHOLD = 400;
export const RUBBER_BAND_PX = 48;

/** Minimum movement before locking pager pan vs vertical scroll. */
export const PAGER_PAN_LOCK_PX = 10;

/** |dx| must exceed |dy| × ratio to count as a horizontal pager pan. */
export const PAGER_PAN_HORIZONTAL_RATIO = 1.5;

export type SwipeDecision = "prev" | "next" | "cancel";
export type SwipeDirection = "prev" | "next";
export type PagerPanIntent = "pending" | "pan" | "scroll";

/** Center panel offset for a 3-panel horizontal track (prev | current | next). */
export function pagerRestX(width: number): number {
  return -width;
}

export function pagerCommitTargetX(width: number, direction: SwipeDirection): number {
  return direction === "prev" ? 0 : -2 * width;
}

export function classifyPagerPanIntent(dx: number, dy: number): PagerPanIntent {
  const absX = Math.abs(dx);
  const absY = Math.abs(dy);

  if (absX < PAGER_PAN_LOCK_PX && absY < PAGER_PAN_LOCK_PX) {
    return "pending";
  }

  if (absY > absX * PAGER_PAN_HORIZONTAL_RATIO) {
    return "scroll";
  }

  if (absX > absY * PAGER_PAN_HORIZONTAL_RATIO) {
    return "pan";
  }

  return "scroll";
}

export function pointerVelocityX(dx: number, elapsedMs: number): number {
  return (dx / Math.max(elapsedMs, 1)) * 1000;
}

/** Swipe right → prev, swipe left → next (timeline-style). */
export function resolveSwipeRelease(
  offsetX: number,
  velocityX: number,
  canGoPrev: boolean,
  canGoNext: boolean,
): SwipeDecision {
  if (canGoPrev && (offsetX >= SWIPE_THRESHOLD_PX || velocityX >= SWIPE_VELOCITY_THRESHOLD)) {
    return "prev";
  }
  if (canGoNext && (offsetX <= -SWIPE_THRESHOLD_PX || velocityX <= -SWIPE_VELOCITY_THRESHOLD)) {
    return "next";
  }
  return "cancel";
}

export function pagerDragConstraints(
  width: number,
  canGoPrev: boolean,
  canGoNext: boolean,
): { left: number; right: number } {
  const rest = -width;
  return {
    left: canGoNext ? rest - width : rest - RUBBER_BAND_PX,
    right: canGoPrev ? rest + width : rest + RUBBER_BAND_PX,
  };
}

export function clampPagerPanX(
  x: number,
  width: number,
  canGoPrev: boolean,
  canGoNext: boolean,
): number {
  const { left, right } = pagerDragConstraints(width, canGoPrev, canGoNext);
  return Math.min(right, Math.max(left, x));
}

export function pagerDragElastic(canGoPrev: boolean, canGoNext: boolean) {
  return {
    left: canGoNext ? 0.08 : 0.32,
    right: canGoPrev ? 0.08 : 0.32,
  };
}

/** Swipe up → prev, swipe down → next (calendar month grid). */
export function resolveVerticalSwipeRelease(
  offsetY: number,
  velocityY: number,
  canGoPrev: boolean,
  canGoNext: boolean,
): SwipeDecision {
  if (canGoPrev && (offsetY <= -SWIPE_THRESHOLD_PX || velocityY <= -SWIPE_VELOCITY_THRESHOLD)) {
    return "prev";
  }
  if (canGoNext && (offsetY >= SWIPE_THRESHOLD_PX || velocityY >= SWIPE_VELOCITY_THRESHOLD)) {
    return "next";
  }
  return "cancel";
}

export function pagerDragConstraintsY(
  height: number,
  canGoPrev: boolean,
  canGoNext: boolean,
): { top: number; bottom: number } {
  const rest = -height;
  return {
    top: canGoPrev ? rest - height : rest - RUBBER_BAND_PX,
    bottom: canGoNext ? rest + height : rest + RUBBER_BAND_PX,
  };
}

export function pagerDragElasticY(canGoPrev: boolean, canGoNext: boolean) {
  return {
    top: canGoPrev ? 0.08 : 0.32,
    bottom: canGoNext ? 0.08 : 0.32,
  };
}

/** Skip pager capture when the ring is in focus mode or the target is a segment hit area. */
export function shouldIgnorePagerPointerTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as { closest?: unknown }).closest !== "function") return false;
  const element = target as unknown as { closest(selector: string): Element | null };
  return (
    element.closest("[data-today-ring-focus-root]") !== null
    || element.closest("[data-ring-segment-hit]") !== null
  );
}
